import { Auth0Provider } from '@auth0/auth0-react';
import { ConfigurationError } from '@pn/core/errors';
import { isEmbedded } from '@pn/core/utils/embedded';
import { isCordova } from '@pn/core/utils/env';
import React from 'react';
import { Auth0CallbackListener } from './Auth0CallbackListener';

const domain = import.meta.env.VITE_APP_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_APP_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_APP_AUTH0_AUDIENCE;

type Props = {
  children: React.ReactNode;
  navigate: (path: string) => void;
};

export const Auth0AuthProvider = ({ children, navigate }: Props) => {
  if (!domain || !clientId || !audience) {
    throw new ConfigurationError('Auth0 environment variables are not set');
  }

  /**
   * Goes back to last visited URL after login.
   */
  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    navigate(appState?.returnTo ?? window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
        scope: 'openid profile email offline_access',
      }}
      useCookiesForTransactions={isEmbedded()} // https://github.com/auth0/auth0-spa-js/issues/593
      /**
       * Refresh token must survive reloads to avoid re-triggering
       * iframe-based silent auth (also blocked by 3rd-party-cookie
       * policies); pairs with refresh token rotation (Auth0 dashboard)
       * to limit exposure.
       */
      cacheLocation="localstorage"
      useRefreshTokens
      /**
       * Cordova logs in through SafariViewController, so the Auth0 session
       * cookie never reaches the WebView and the iframe can only time out.
       * Without the fallback a missing refresh token fails immediately.
       */
      useRefreshTokensFallback={!isCordova()}
      /**
       * The iframe fallback above cannot succeed where 3rd-party cookies are
       * blocked, so fail well short of the 60s default rather than leave the
       * user waiting on a request that is already lost.
       */
      authorizeTimeoutInSeconds={10}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
      <Auth0CallbackListener />
    </Auth0Provider>
  );
};
